import { XMarkIcon } from '@heroicons/react/16/solid';
import { CheckIcon } from '@heroicons/react/20/solid';
import MenuIcon from '@heroicons/react/20/solid/Bars3Icon';
import dayjs from 'dayjs';
import HTML from 'html-parse-stringify';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { EMPTY_OBJECT, TIMEZONE } from '../../../shared/constants';
import { healthQuestions } from '../constants/health-questions';
import { FORMAT_LLLL, FORMAT_LT } from '../constants/misc';
import { preferenceQuestions } from '../constants/preference-questions';
import { useInviteStore } from '../store/use-invite-store';
import { Dialog, DialogClose, DialogContent, DialogHeading } from './dialog';
import { NextButton } from './ui/next-button';
import { UncontrolledSelect } from './ui/select';

const bookmarkSteps = [
  {
    path: /\/(specialist|appointment|appointment-reserved)$/,
    to: 'specialist',
    labelKey: 'step.appointment',
    status: (_, invite) => (invite.appointment ? 'done' : 'todo'),
    useStepDetails: useAppointmentDetails,
  },
  {
    path: /\/(name-birthdate|nhi-residency|home-address|contact-details|doctor|emergency)$/,
    to: 'name-birthdate',
    labelKey: 'step.details',
    status: ({ patient, gp, emergency }) => {
      const completed = [
        patient?.dateOfBirth,
        typeof patient?.isResident === 'boolean',
        patient?.address,
        patient?.email,
        gp,
        emergency?.fullName,
      ].filter(Boolean);
      return completed.length === 6 ? 'done' : completed.length === 0 ? 'todo' : 'doing';
    },
    useStepDetails: useNoDetails,
  },
  {
    path: /\/insurance$/,
    to: 'insurance',
    labelKey: 'step.insurance',
    status: ({ insurance }) => (typeof insurance?.termsAccepted === 'boolean' ? 'done' : 'todo'),
    useStepDetails: useInsuranceDetails,
  },
  {
    path: /\/health-[a-z-]+$/,
    to: 'health-questions',
    labelKey: 'step.health-questions',
    status: (...args) => {
      const total = healthQuestions.length;
      const done = healthQuestions.reduce((acc, q) => acc + (q.done(...args) ? 1 : 0), 0);
      return done === total ? 'done' : done > 0 ? 'doing' : 'todo';
    },
    useStepDetails: useHealthQuestionsDetails,
  },
  {
    path: /\/medications$/,
    to: 'medications',
    labelKey: 'step.medications',
    status: ({ medicationsConfirmed }) => (medicationsConfirmed ? 'done' : 'todo'),
    useStepDetails: useNoDetails,
  },
  {
    path: /\/history$/,
    to: 'history',
    labelKey: 'step.history',
    status: ({ proceduresConfirmed }) => (proceduresConfirmed ? 'done' : 'todo'),
    useStepDetails: useNoDetails,
  },
  {
    path: /\/preference-[a-z-]+$/,
    to: 'preference-mobility',
    labelKey: 'step.preference-questions',
    status: (...args) => {
      const total = preferenceQuestions.length;
      const done = preferenceQuestions.reduce((acc, q) => acc + (q.done(...args) ? 1 : 0), 0);
      return done === total ? 'done' : done > 0 ? 'doing' : 'todo';
    },
    useStepDetails: useNoDetails,
  },
];

export function BookmarksMenu({ nextLabel }) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <footer className="w-full">
      <nav aria-label={t('bookmarksMenu')}>
        <div className="flex">
          <MenuButton onClick={() => setMenuOpen(true)} />
          <NextButton label={nextLabel} />
        </div>
        <MenuDrawer menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      </nav>
    </footer>
  );
}

function MenuButton({ onClick }) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className="flex-shrink-0 size-12.5 flex items-center justify-center text-white bg-primary-active hover:text-white hover:bg-primary-hover active:bg-primary"
      onClick={onClick}
      aria-label={t('openBookmarksMenu')}
    >
      <MenuIcon className="size-7.5" />
    </button>
  );
}

function MenuDrawer({ menuOpen, setMenuOpen }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { invite } = useInviteStore();
  const bookmarks = useMemo(() => {
    let current = -1;
    const statuses = bookmarkSteps.map((step, index, array) => {
      let status = step.status(invite.onlineBookingData, invite);
      if (step.path.test(location.pathname)) {
        current = index;
        const prevStatus = array[index - 1]?.status(invite.onlineBookingData, invite) ?? 'done';
        if (status !== 'done' && prevStatus === 'done') {
          status = 'doing';
        }
      }
      return status;
    });
    return (
      <ul className="flex-grow overflow-hidden">
        {bookmarkSteps.map((step, index, array) => (
          <MenuDrawerStepItem
            key={step.labelKey}
            step={{ ...step, status: statuses[index], current: current === index }}
            next={{ ...(array[index + 1] ?? {}), status: statuses[index + 1] ?? 'todo' }}
            prev={{ ...(array[index - 1] ?? {}), status: statuses[index - 1] ?? 'done' }}
            current={current === index}
            setMenuOpen={setMenuOpen}
          />
        ))}
      </ul>
    );
  }, [invite, location.pathname, setMenuOpen]);

  return (
    <Dialog open={menuOpen} onOpenChange={setMenuOpen}>
      <DialogContent className="Dialog fixed flex flex-col top-0 left-0 size-full overflow-y-auto sm:w-[390px] p-5 bg-primary-active text-white">
        <div className="container flex flex-col flex-grow">
          <DialogHeading className="title-sm flex items-start justify-between gap-4 mb-6">
            <span>{t('yourProgress')}</span>
            <DialogClose className="hover:text-primary-light" aria-label={t('closeDialog')}>
              <XMarkIcon className="size-6" aria-hidden="true" />
            </DialogClose>
          </DialogHeading>
          {bookmarks}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MenuDrawerStepItem({ step, prev, next, setMenuOpen }) {
  const { t } = useTranslation();
  const { invite } = useInviteStore();
  const href = step.to && !step.current ? `/${invite.reference}/${step.to}` : null;
  const { summary = null, actions = null } = step.useStepDetails({
    href,
    setMenuOpen,
    status: step.status,
  });

  return (
    <li
      className="pb-[1.5rem] first:mt-0 flex flex-row items-stretch gap-3 group/step"
      data-status={step.status}
      data-prev-status={prev.status}
      data-has-prev={!!prev}
      data-next-status={next.status}
      data-has-next={!!next}
      data-current={step.current}
    >
      <div className="relative flex-grow flex flex-col justify-start w-9 max-w-9">
        <div
          className="flex-shrink-0 size-9 flex items-center justify-center rounded-full border-2 border-success-600 group-data-[status=done]/step:bg-success-600 group-data-[status=doing]/step:bg-light-active group-data-[status=todo]/step:bg-white group-data-[status=todo]/step:border-slate-400 after:top-1/2 after:size-3 after:bg-success-600 after:rounded-full after:hidden group-data-[status=doing]/step:after:block  group-data-[status=todo]/step:opacity-50"
          aria-hidden="true"
        >
          <CheckIcon className="size-5 text-white hidden group-data-[status=done]/step:block" />
        </div>
        <hr className="absolute h-[calc(100%_+_1.5rem_-_theme(spacing.9))] -bottom-[1.5rem] left-1/2 border-l-[2px] border-success-600 -z-1 group-data-[has-next=false]/step:hidden group-data-[next-status=todo]/step:hidden group-data-[next-status=doing]/step:border-white" />
      </div>
      <div className="flex flex-col items-start justify-center w-full">
        <Link
          to={href}
          className="flex flex-col justify-center w-full group-data-[status=todo]/step:opacity-50 group-data-[current=true]/step:font-bold hover:text-inherit"
        >
          <span className="text-small w-full truncate">{t(step.labelKey)}</span>
          <div className="pt-1 flex flex-col group-data-[status=todo]/step:hidden">{summary}</div>
        </Link>
        <div className="flex flex-col w-full group-data-[status=todo]/step:hidden">{actions}</div>
      </div>
    </li>
  );
}

function useNoDetails() {
  return EMPTY_OBJECT;
}

function useAppointmentDetails() {
  const { t } = useTranslation('page.specialist');
  const { invite } = useInviteStore();
  const { appointment } = invite;
  return useMemo(() => {
    if (appointment) {
      const date = dayjs.tz(
        `${appointment.appointmentDate}T${appointment.appointmentTime}`,
        TIMEZONE,
      );
      const practitionerId = invite.appointment?.practitioner?.practitionerId ?? 'unknown';
      return {
        summary: (
          <>
            <span>
              {t('common:formattedDate', {
                date: date?.format(FORMAT_LLLL),
                time: date?.format(FORMAT_LT),
              })}
            </span>
            <span>{t(`specialists.${practitionerId}.name`)}</span>
          </>
        ),
      };
    } else {
      return {};
    }
  }, [appointment, invite.appointment?.practitioner?.practitionerId, t]);
}

function useInsuranceDetails() {
  const { t } = useTranslation('page.insurance');
  const { invite } = useInviteStore();
  const insurer = invite.onlineBookingData?.insurance?.healthInsurer;
  return useMemo(() => {
    if (insurer) {
      return {
        summary: t(`insurerSelect.${insurer}`),
      };
    } else {
      return {};
    }
  }, [insurer, t]);
}

function isTextNode(node) {
  return node.type === 'text';
}

function findTextNodeText(list) {
  for (const node of list) {
    if (isTextNode(node)) {
      return node.content;
    } else if (node.children?.length > 0) {
      const text = findTextNodeText(node.children);
      if (typeof text === 'string') {
        return text;
      }
    }
  }
}

// The HTML parsing here is not great but we would need to restructure the
// translations to avoid it.
function useHealthQuestionLabels() {
  const { t } = useTranslation('page.health-questions');
  return useMemo(
    () =>
      healthQuestions.map((question) => {
        const key = question.props.translationKey;
        const translation = t(`${key}.title`);
        const html = HTML.parse(translation);
        return findTextNodeText(html) ?? key;
      }),
    [t],
  );
}

function useHealthQuestionsDetails({ status, setMenuOpen }) {
  const { t } = useTranslation('page.health-questions');
  const navigate = useNavigate();
  const { invite } = useInviteStore();
  const labels = useHealthQuestionLabels();
  return useMemo(() => {
    let summary = null;
    let options = null;

    if (status !== 'done') {
      const { dropdownOptions, progressIndex } = healthQuestions.reduce(
        (acc, question, index) => {
          const done = question.done(invite.onlineBookingData, invite);
          acc.dropdownOptions.push({
            value: question.path,
            label: labels[index],
            done,
            disabled: !done && !(acc.dropdownOptions.at(-1)?.done ?? true),
          });
          acc.progressIndex = Math.min(acc.progressIndex, done ? healthQuestions.length : index);
          return acc;
        },
        {
          dropdownOptions: [],
          progressIndex: healthQuestions.length - 1,
        },
      );
      summary = (
        <span>
          At {labels[progressIndex]} ({progressIndex + 1}/{healthQuestions.length})
        </span>
      );
      options = dropdownOptions;
    } else if (status === 'done') {
      const { dropdownOptions, questionsYes } = healthQuestions.reduce(
        (acc, question, index) => {
          const primary = question.answer(invite.onlineBookingData, invite)?.primary;
          if (primary && primary !== 'no') {
            acc.questionsYes.push(
              <span key={index} className="last:after:content-none after:content-[','] mr-1">
                {labels[index]}
              </span>,
            );
          }
          acc.dropdownOptions.push({
            value: question.path,
            label: labels[index],
            done: true,
            disabled: false,
          });
          return acc;
        },
        { dropdownOptions: [], questionsYes: [] },
      );
      summary =
        questionsYes.length > 0 ? (
          <span className="italic">
            {t('common:yesTo')}: {questionsYes}
          </span>
        ) : null;
      options = dropdownOptions;
    }

    return {
      summary,
      actions: (
        <UncontrolledSelect
          placeholder={t('dropLabel')}
          options={options}
          onChange={(path) => {
            navigate(`/${invite.reference}/${path}`);
            setMenuOpen(false);
          }}
        />
      ),
    };
  }, [invite, labels, navigate, setMenuOpen, status, t]);
}
